import React from 'react';
import Table from '../../component/table/table';
import { MUIDataTableColumnDef  } from 'mui-datatables';
import './scss/customer.scss';
import CustomerPageToolbar from './customer-page-toolbar.component';
import { Box, Grid, Skeleton, Typography } from '@mui/material';
import setLoading from '../../component/linear-determinate';
import { apiManage_rbp } from '../../request';
import GlobalNotification from '../../component/common/snackbar';
import Card from '../dish-menu-creator/Card';
import BaseInput from '../../component/form/base-input.component';
import { FormFieldCell } from '../../component/form/form-field-cell.component';
import _ from 'lodash';
import i18n from '../../i18n';
import { Customer } from '../../models/customer.entity';
import ButtonComponent from '../../component/button/button.component';
import DeleteDialogComponent from '../../component/dialog/delete-dialog.component';

class CustomerPage extends React.Component<Props, State> {
    public tableRef: React.RefObject<any> = React.createRef();
    public state: State = {
        rowsSelected: [],
        pagPage: 0,
        pagPerPage: 0,
        total: 0,
        /** 接口返回的完整顾客列表（前端智能搜索 / 查重基于此） */
        allCustomers: [],
        isLoading: false,
        deleteOpen: false,
        //单行删除的弹窗
        deleteOpenOne: false,
        //单行删除的id
        deleteOpenOneId: 0,
        /** 智能搜索：模糊匹配姓名、电话 */
        smartSearchText: '',
        /** 智能查重：按电话/姓名/地址去重后仅展示应保留的联系人 */
        dedupEnabled: false,
        isCheckDetails: false,
        detail: {},
        // 表格每页行数
        rowsPerPage: 20,
        deleteIds: [],
        currPage: 0,
        // 保存时改成1 控制是否校验无值
        isEmpty: 0
    }

    constructor(props: Props) {
        super(props);
        this.handleTableToolbarSelectRender = this.handleTableToolbarSelectRender.bind(this);
    }

    // ***********************
    // Life Cycle Function
    // ***********************
    componentDidMount() {
        this.setState({
            isLoading: true
        }, async () => {
            await this.init();
        })
    }

    // *********************
    // Default Function
    // *********************

    async init() {
        try {
            setLoading(true);
            const result = await apiManage_rbp.get_customer({});
            setLoading(false);
            if (!result.error) {
                this.setState({
                    isLoading: false,
                    allCustomers: result || [],
                    rowsSelected: []
                })
            } else {
                // 获取失败
                this.setState({
                    isLoading: false,
                    allCustomers: [],
                    rowsSelected: []
                })
            }

        } catch (error) {
            setLoading(false);
            this.setState({
                isLoading: false
            })
        }
    }

    /**
     * 「智能查重」：链式去重（先电话 → 再姓名 → 再地址）。
     * 每一轮只在**当前列表**里按该维度分组，同键只保留列表中**最先出现**的一条；无主号/备用号/姓名/地址的条目不参与该轮分桶，原样进入下一轮。
     * 主号、备用号均参与电话键。地址与智能搜索一致。
     */
    computeDuplicateCustomers(customers: Customer[]): Customer[] {
        const normalizeTelephoneRaw = (raw: string) =>
            (raw || '').replace(/\s/g, '').trim();

        const telKeys = (item: Customer): string[] => {
            const keys: string[] = [];
            const t1 = normalizeTelephoneRaw(item.telephone_1 || '');
            if (t1) {
                keys.push(`t:${t1}`);
            }
            const t2 = normalizeTelephoneRaw(item.telephone_2 || '');
            if (t2) {
                keys.push(`t:${t2}`);
            }
            return keys;
        };

        const normalizeName = (item: Customer) =>
            (item.customer_name || '').trim().toLowerCase();
        /** 与表格智能搜索一致：三段地址去空白后拼接再小写 */
        const normalizeAddress = (item: Customer) =>
            [item.address, item.address2, item.address3]
                .map((s) => (s || '').replace(/\s/g, '').toLowerCase())
                .join('');

        /** 按维度分桶，同键保留当前列表顺序下第一条；无键则整行保留 */
        const dedupeRound = (rows: Customer[], getKeys: (item: Customer) => string[]): Customer[] => {
            const buckets: Record<string, Customer[]> = {};
            for (const item of rows) {
                for (const k of getKeys(item)) {
                    if (!k) {
                        continue;
                    }
                    if (!buckets[k]) {
                        buckets[k] = [];
                    }
                    buckets[k].push(item);
                }
            }
            const winnerIds = new Set<number>();
            for (const k of Object.keys(buckets)) {
                const first = buckets[k][0];
                if (first?.customer_id != null) {
                    winnerIds.add(first.customer_id);
                }
            }
            return rows.filter((item) => {
                const keys = getKeys(item);
                if (!keys.length) {
                    return true;
                }
                const id = item.customer_id;
                return id != null && winnerIds.has(id);
            });
        };

        let stage = [...customers];
        stage = dedupeRound(stage, (item) => telKeys(item));
        stage = dedupeRound(stage, (item) => {
            const n = normalizeName(item);
            return n ? [`n:${n}`] : [];
        });
        stage = dedupeRound(stage, (item) => {
            const a = normalizeAddress(item);
            return a ? [`a:${a}`] : [];
        });

        const seenId = new Set<number>();
        const repeatList: Customer[] = [];
        for (const item of stage) {
            const id = item.customer_id;
            if (id != null && !seenId.has(id)) {
                seenId.add(id);
                repeatList.push(item);
            }
        }
        return repeatList;
    }

    /**
     * 当前表格展示数据：前端模糊搜索姓名/电话/地址，可选开启去重展示
     */
    getDisplayCustomers(): Customer[] {
        const { allCustomers, smartSearchText, dedupEnabled } = this.state;
        const list = allCustomers || [];
        const q = (smartSearchText || '').trim().toLowerCase();
        let filtered = list;
        if (q) {
            const qCompact = q.replace(/\s/g, '');
            filtered = list.filter((item) => {
                const name = (item.customer_name || '').toLowerCase();
                const tel = (item.telephone_1 || '').toLowerCase().replace(/\s/g, '');
                const addressHaystack = [item.address, item.address2, item.address3]
                    .map((s) => (s || '').toLowerCase().replace(/\s/g, ''))
                    .join('');
                return name.includes(q) || tel.includes(qCompact) || addressHaystack.includes(qCompact);
            });
        }
        if (!dedupEnabled) {
            return filtered;
        }
        /** 去重代表条在全量上计算后再与筛选结果相交，避免只看 filtered 时分组不全 */
        const duplicateRowsFull = this.computeDuplicateCustomers(list);
        const duplicateIdSet = new Set(
            duplicateRowsFull.map((c) => c.customer_id).filter((id): id is number => id != null),
        );
        return filtered.filter((c) => c.customer_id != null && duplicateIdSet.has(c.customer_id));
    }

    /**
     * 批量删除顾客
     */
    async handleDeleteCus() {
        try {
            setLoading(true);
            const { deleteIds } = this.state;
            // let ids: number[] = [];
            // for (let i = 0; i < this.state.rowsSelected.length; i++) {
            //     ids.push(this.state.tables[this.state.rowsSelected[i]].customer_id || 0)
            // }
            const result = await apiManage_rbp.del_customer(deleteIds);
            if (result.fieldCount === 0 && result.affectedRows > 0) {
                GlobalNotification.success(i18n.t('google_text_delete_success'))
                await this.init()
            } else {
                GlobalNotification.error(i18n.t('google_text_delete_failed'))
            }
            setLoading(false);
        } catch (error) {

        }
    }

    /**
     * 删除顾客
     */
    async deleteCus() {
        try {
            setLoading(true);
            const { deleteOpenOneId, deleteIds } = this.state;
            const result = !deleteOpenOneId && deleteIds.length
                ? await apiManage_rbp.batch_del_customer(deleteIds)
                : await apiManage_rbp.del_one_customer(deleteOpenOneId);
            if (result.fieldCount === 0 && result.affectedRows > 0) {
                GlobalNotification.success(i18n.t('google_text_delete_success'))
                await this.init()
            } else {
                GlobalNotification.error(i18n.t('google_text_delete_failed'))
                setLoading(false);
            }
        } catch (error) {

        }
    }

    /**
     *
     * @param value
     * @param key
     */

    onChange(value: number | string | string[], key: string) {
        const { detail } = this.state;
        const newValue = value;
        _.set(detail, key, newValue);
        this.setState({
            detail
        })
    }

    /**
     * 修改顾客信息
     */
    async savaCustomer() {
        try {
            if (!this.state.detail.customer_name || !this.state.detail.telephone_1) {
                this.setState({ isEmpty: 1 });
                return
            }
            setLoading(true);
            const result = await apiManage_rbp.patch_customer(this.state.detail);
            if (!result.error) {
                GlobalNotification.success(i18n.t('google_text_update_successfully'))
                this.setState({
                    isCheckDetails: false
                }, async () => {
                    await this.init()
                })
            } else {
                GlobalNotification.error(i18n.t('google_text_verify_failed'))
                this.setState({
                    isCheckDetails: false
                })
            }
            setLoading(false);
        } catch (error) {

        }
    }

    /**
     * 选中项，表格头部工具栏显示
     * @returns
     */
    public handleTableToolbarSelectRender(
        selectedRows: { data: Array<{ index: number; dataIndex: number }>; lookup: { [key: number]: boolean } }
    ): React.ReactNode {
        // 选中的数据
        const rows = this.getDisplayCustomers();
        const dishSelected = selectedRows.data.map((item) => (rows[item.dataIndex]?.customer_id || 0))
        return (
            <Box padding={'4px 12px'}>
                <ButtonComponent
                    action='DELETE'
                    handleClick={() => {
                        this.setState({
                            deleteOpen: true,
                            deleteIds: dishSelected
                        })
                    }}
                />
            </Box>
        )
    }

    // *********************
    // VIEW
    // *********************

    render() {
        const { rowsSelected, isLoading, deleteOpen, isCheckDetails, detail, rowsPerPage } = this.state;
        const displayCustomers = this.getDisplayCustomers();
        const columns: MUIDataTableColumnDef[] = [
            {
                name: 'customer_id',
                label: i18n.t('google_text_id'),
                options: {
                    sort: false,
                }
            },
            {
                name: 'customer_name',
                label: i18n.t('customers_page_name'),
            },
            {
                name: 'telephone_1',
                label: i18n.t('customers_page_phone'),
                options: {
                    sort: false,
                }
            },
            {
                name: 'address',
                label: i18n.t('customers_page_address'),
                options: {
                    sort: false,
                }
            },
            {
                name: '',
                label: i18n.t('general_text_operating'),
                options: {
                    sort: false,
                    customBodyRenderLite: (dataIndex: number) => {
                        return (
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                                <ButtonComponent
                                    variant='text'
                                    action='EDIT'
                                    space={false}
                                    handleClick={() => {
                                        this.setState({
                                            isCheckDetails: true,
                                            detail: _.cloneDeep(displayCustomers[dataIndex])
                                        })
                                    }}
                                />
                                <ButtonComponent
                                    variant='text'
                                    action='DELETE'
                                    space={false}
                                    handleClick={() => {
                                        this.setState({ deleteOpen: true })
                                        //打开弹窗设置删除的行主键
                                        this.setState({ deleteIds: [displayCustomers[dataIndex]?.customer_id || 0] })
                                    }}
                                />
                            </div>
                        )
                    }
                }
            },
        ];

        const formArr = [
            {
                key: 'customer_id',
                label: i18n.t('customers_page_customer_id'),
                disabled: true,
            },
            {
                key: 'customer_name',
                label: i18n.t('customers_page_customer_name'),
                required: true,
                regularType: 'required'
            },
            {
                key: 'telephone_1',
                label: i18n.t('customers_page_phone'),
                required: true,
                regularType: 'required'
            },
            {
                key: 'email',
                label: i18n.t('restaurant_email'),
            },
            {
                key: 'address',
                label: i18n.t('customers_page_address'),
            },
            {
                key: 'required',
                label: i18n.t('customers_page_dinning_habbits_info'),
            },
        ];

        return (
            <Box className='customer'>
                {/* <--单行删除的弹窗控制--> */}
                {
                    // deleteOpenOne &&
                    // <AlertDialog
                    //     open={true}
                    //     title={i18n.t('customers_page_delete_customer')}
                    //     onCancel={() => {
                    //         this.setState({
                    //             deleteOpenOne: false
                    //         })
                    //     }}
                    //     onConfirm={() => {
                    //         this.setState({
                    //             deleteOpenOne: false
                    //         }, () => {
                    //             this.deleteCus()
                    //         })

                    //     }}
                    // />
                }
                {
                    isLoading ? (
                        renderCustomerPageSkeleton()
                    ) : isCheckDetails ?
                        <Card
                            title={(
                                <Box display='flex' alignItems='center'>
                                    <ButtonComponent
                                        action='BACK'
                                        handleClick={() => {
                                            this.setState({
                                                isCheckDetails: false
                                            }, async () => {
                                                await this.init();
                                            })
                                        }}
                                    />
                                    <Typography variant='subtitle1' className='text'>
                                        {i18n.t('customers_page_modify_customer_info')}
                                    </Typography>
                                </Box>
                            )}
                            actionRender={(
                                <ButtonComponent
                                    action='SAVE'
                                    handleClick={async () => {
                                        await this.savaCustomer()
                                    }}
                                />
                            )}
                            contentRender={(
                                <Grid container spacing={2} alignItems='stretch'>
                                    {(formArr || []).map((item) => (
                                        <Grid key={item.key} item xs={12} md={6}>
                                            <FormFieldCell label={item.label}>
                                                <BaseInput
                                                    hiddenLabel
                                                    disabled={item.disabled}
                                                    variant='outlined'
                                                    fullWidth
                                                    size='small'
                                                    isEmpty={!!this.state.isEmpty}
                                                    required={item.required}
                                                    regularType={item.regularType}
                                                    value={_.get(detail, `${item.key}`, '')}
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                        this.onChange(event.target.value, item.key)
                                                    }}
                                                />
                                            </FormFieldCell>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        />
                        :
                        <Box className='customer-page__shell'>
                            <Box className='customer-page__header'>
                                <Typography className='customer-page__title' component='h2'>
                                    {i18n.t('customers_page_info')}
                                </Typography>
                                <CustomerPageToolbar
                                    searchText={this.state.smartSearchText}
                                    onSearchTextChange={(smartSearchText) => this.setState({ smartSearchText })}
                                    dedupEnabled={this.state.dedupEnabled}
                                    onDedupChange={(dedupEnabled) => this.setState({ dedupEnabled })}
                                />
                            </Box>
                            <Box className='customer-page__table-card'>
                                <Table
                                    ref={this.tableRef}
                                    centerCellContent
                                    data={displayCustomers}
                                    columns={columns}
                                    selectableRowsHideCheckboxes={false}
                                    pagination={true}
                                    rowsPerPage={rowsPerPage}
                                    page={this.state.currPage}
                                    onChangePage={(page: number) => {
                                        this.setState({ currPage: page })
                                    }}
                                    rowsSelected={rowsSelected}
                                    selectableRowsHeader={true}
                                    selectToolbarPlacement='replace'
                                    onRowSelectionChange={(currentRowsSelected: any[], allRowsSelected: any[], rowsSelected?: number[]) => {
                                        this.setState({
                                            rowsSelected: rowsSelected || []
                                        })
                                    }}
                                    customToolbarSelect={(
                                        selectedRows: { data: Array<{ index: number; dataIndex: number }>; lookup: { [key: number]: boolean } },
                                    ): React.ReactNode => {
                                        return this.handleTableToolbarSelectRender(selectedRows);
                                    }}
                                    otherOption={{
                                        onChangeRowsPerPage: (nums: number) => {
                                            this.setState({
                                                rowsPerPage: nums,
                                            })
                                        },
                                        // onRowsDelete: (rowsDeleted, newTableData) => {
                                        //     this.setState({
                                        //         deleteOpen: true,
                                        //         deleteIds: (rowsDeleted.data.map((item) => (
                                        //             _.get(tables, `[${item.dataIndex}].customer_id`)
                                        //         )) || []) as unknown as number[]
                                        //     })
                                        // },
                                        onRowSelectionChange: (currentRowsSelected: any[], allRowsSelected: any[], rowsSelected?: any[] | undefined) => {
                                            // 只选中当前页的数据
                                            if (this.tableRef.current && this.tableRef.current.baseTableRefs.current) {
                                                const { page, rowsPerPage } = this.tableRef.current.baseTableRefs.current.state
                                                const firstIndex = page * rowsPerPage
                                                const lastIndex = (page + 1) * rowsPerPage
                                                currentRowsSelected = currentRowsSelected.slice(firstIndex, lastIndex)
                                                allRowsSelected = allRowsSelected.slice(firstIndex, lastIndex)
                                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                                rowsSelected = rowsSelected?.slice(firstIndex, lastIndex)
                                                this.tableRef.current.baseTableRefs.current.setState({
                                                    selectedRows: {
                                                        data: allRowsSelected,
                                                        lookup: allRowsSelected.reduce((res, item) => { res[item.dataIndex] = true; return res }, {})
                                                    }
                                                })
                                            }
                                        }
                                    }}
                                />
                            </Box>
                        </Box>

                }
                {
                    deleteOpen &&
                    <DeleteDialogComponent
                        {...{
                            title: i18n.t('google_text_public_button_delete'),
                            open: true,
                            onCancel: () => {
                                this.setState({
                                    deleteOpen: false
                                })
                            },
                            onConfirm: async () => {
                                await this.deleteCus()
                                this.setState({
                                    deleteOpen: false
                                })
                            },
                        }}
                    />
                }
            </Box>
        )
    }
}

export default CustomerPage;

// ***************
// TYPE
// ***************

type State = {
    rowsSelected: any[];
    pagPage: number;
    pagPerPage: number;
    total: number;
    allCustomers: Array<Customer>;
    isLoading: boolean;
    deleteOpen: boolean;
    deleteOpenOne: boolean;
    deleteOpenOneId: number;
    smartSearchText: string;
    dedupEnabled: boolean;
    isCheckDetails: boolean;
    detail: Customer;
    // 表格每页行数
    rowsPerPage: number;
    deleteIds: number[];
    currPage: number;
    // 保存时改成1 控制是否校验无值
    isEmpty: number;
}

type Props = {}

/** 顾客页骨架屏（列表态）：标题/工具栏 + 表格头 + 行占位 */
function renderCustomerPageSkeleton(): React.ReactNode {
    return (
        <Box className='customer-page__shell'>
            <Box className='customer-page__header'>
                <Skeleton variant='text' width={180} height={44} />
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Skeleton variant='rounded' width={240} height={40} />
                    <Skeleton variant='rounded' width={120} height={40} />
                </Box>
            </Box>
            <Box className='customer-page__table-card' sx={{ p: 2 }}>
                <Skeleton variant='rounded' height={38} sx={{ mb: 1.5 }} />
                {Array.from({ length: 8 }).map((_, index) => (
                    <Skeleton
                        key={`customer-page-row-skeleton-${index}`}
                        variant='rounded'
                        height={36}
                        sx={{ mb: 1 }}
                    />
                ))}
            </Box>
        </Box>
    );
}
